import { useState } from "react";
import {
  CheckCircleOutlined,
  DownloadOutlined,
  HeartOutlined,
  HolderOutlined,
  ShoppingCartOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card, DatePicker, Space, Switch, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { MousePointer } from "lucide-react";

import { FunnelStepType, type SalesFunnel, type SalesFunnelStep } from "@fitness-app/data-models/entities/SalesFunnel";

import { getPriceFormatter } from "~/modules/Products/utils/getPriceFormatter";

// Configure dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Title } = Typography;
const priceFormatter = getPriceFormatter("PLN");

// Helper function to get step metrics from funnel stats
const getStepMetrics = (
  stepId: string,
  funnelStats: ProductSalesFunnelsDetailsProps["funnelStats"],
  excludeTestEvents: boolean,
): { views: number; conversions: number; revenue: number; landingConversions: number } => {
  if (!funnelStats?.stats) {
    return { views: 0, conversions: 0, revenue: 0, landingConversions: 0 };
  }

  // Get production stats for this step
  const productionStats = funnelStats.stats[stepId] || {
    viewed: 0,
    purchased: 0,
    rejected: 0,
    revenue: 0,
    conversion: 0,
  };

  // Get test mode stats for this step
  const testmodeStats = funnelStats.testmodeStats?.[stepId] || {
    viewed: 0,
    purchased: 0,
    rejected: 0,
    revenue: 0,
    conversion: 0,
  };

  // Calculate final stats based on excludeTestEvents flag
  let views = productionStats.viewed;
  let conversions = productionStats.purchased;
  let revenue = productionStats.revenue;
  let landingConversions = productionStats.conversion;

  if (excludeTestEvents) {
    // When excluding test events, subtract test mode stats from totals (which already include test events)
    views = Math.max(0, views - testmodeStats.viewed);
    conversions = Math.max(0, conversions - testmodeStats.purchased);
    revenue = Math.max(0, revenue - testmodeStats.revenue);
    landingConversions = Math.max(0, landingConversions - testmodeStats.conversion);
  }
  // When includeTestEvents is true, we use the productionStats as-is (they already include test events)

  return { views, conversions, revenue, landingConversions };
};

// Helper function to get step badges
const getStepBadges = (
  step: SalesFunnelStep,
  allSteps: SalesFunnelStep[],
): Array<{ text: string; color: string; tooltip?: string }> => {
  const badges: Array<{ text: string; color: string; tooltip?: string }> = [];

  // Check if step has product assigned
  if ("productId" in step.stepConfig || "prices" in step.stepConfig || step.type === FunnelStepType.ThankYou) {
    // Product is assigned, no error badge needed
  } else {
    badges.push({ text: "No Product Assigned", color: "error" });
  }

  // Add conditional badges based on step type
  if (step.type === "upsell" || step.type === "downsell") {
    if (step.stepConfig?.offerAcceptedNextStepId) {
      const nextStep = allSteps.find((s) => s.id === step.stepConfig?.offerAcceptedNextStepId);
      const nextStepPath = nextStep?.path || "Unknown step";
      badges.push({
        text: "Offer Accepted",
        color: "success",
        tooltip: `Leads to: ${nextStepPath}`,
      });
    }
    if (step.stepConfig?.offerRejectedNextStepId) {
      const nextStep = allSteps.find((s) => s.id === step.stepConfig?.offerRejectedNextStepId);
      const nextStepPath = nextStep?.path || "Unknown step";
      badges.push({
        text: "Offer Rejected",
        color: "warning",
        tooltip: `Leads to: ${nextStepPath}`,
      });
    }
  }

  return badges;
};

// Sortable Item Component
const SortableItem = ({
  step,
  funnelStats,
  allSteps,
  excludeTestEvents,
}: {
  step: SalesFunnelStep;
  funnelStats?: ProductSalesFunnelsDetailsProps["funnelStats"];
  allSteps: SalesFunnelStep[];
  excludeTestEvents: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
    disabled: true,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <FunnelStepCard
        step={step}
        dragHandleProps={{ ...attributes, ...listeners }}
        isDragging={isDragging}
        funnelStats={funnelStats}
        allSteps={allSteps}
        excludeTestEvents={excludeTestEvents}
      />
    </div>
  );
};

// Individual Funnel Step Card Component
const FunnelStepCard = ({
  step,
  dragHandleProps,
  isDragging,
  funnelStats,
  allSteps,
  excludeTestEvents,
}: {
  step: SalesFunnelStep;
  dragHandleProps: any;
  isDragging: boolean;
  funnelStats?: ProductSalesFunnelsDetailsProps["funnelStats"];
  allSteps: SalesFunnelStep[];
  excludeTestEvents: boolean;
}) => {
  // Icon mapping based on step type
  const getStepIcon = (type: string) => {
    const iconMap = {
      landing: MousePointer,
      checkout: ShoppingCartOutlined,
      upsell: UploadOutlined,
      downsell: DownloadOutlined,
      thank_you: HeartOutlined,
    };
    return iconMap[type as keyof typeof iconMap] || CheckCircleOutlined;
  };

  // Background color based on step type
  const getStepColor = (type: string) => {
    const colorMap: Record<string, { bg: string; icon: string }> = {
      landing: { bg: "#e6f4ff", icon: "#1890ff" },
      checkout: { bg: "#fff7e6", icon: "#fa8c16" },
      upsell: { bg: "#f6ffed", icon: "#52c41a" },
      downsell: { bg: "#fff1f0", icon: "#ff4d4f" },
      thank_you: { bg: "#f9f0ff", icon: "#722ed1" },
    };
    return colorMap[type] || { bg: "#f5f5f5", icon: "#8c8c8c" };
  };

  const formatMetricValue = (value: number | null | undefined) => {
    if (value === 0) return "0";
    if (value === null || value === undefined) return "-";
    return value.toString();
  };

  const formatPriceValue = (value: number | null | undefined) => {
    if (value === 0) return "0";
    if (value === null || value === undefined) return "-";
    return priceFormatter.format(value / 100);
  };

  const Icon = getStepIcon(step.type);
  const colors = getStepColor(step.type);

  // Get actual metrics and badges
  const metrics = getStepMetrics(step.id, funnelStats, excludeTestEvents);
  const badges = getStepBadges(step, allSteps);

  return (
    <Card
      style={{
        cursor: isDragging ? "grabbing" : "grab",
        boxShadow: isDragging ? "0 4px 12px rgba(0,0,0,0.15)" : undefined,
      }}
      bodyStyle={{ padding: "24px" }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {/* Drag Handle */}
        <div
          {...dragHandleProps}
          style={{
            marginRight: "16px",
            cursor: isDragging ? "grabbing" : "grab",
            padding: "4px",
            borderRadius: "4px",
          }}
        >
          <HolderOutlined style={{ color: "#bfbfbf", fontSize: "16px" }} />
        </div>

        {/* Left Section - Icon and Info */}
        <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <div
            style={{
              padding: "16px",
              borderRadius: "8px",
              backgroundColor: colors.bg,
              marginRight: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon style={{ fontSize: "24px", color: colors.icon }} />
          </div>

          <div style={{ flex: 1 }}>
            <Space direction="vertical" size={4}>
              <Text type="secondary" style={{ fontSize: "12px", textTransform: "uppercase" }}>
                {step.type.replace("_", " ")}
              </Text>
              <Title level={4} style={{ margin: 0, fontSize: "18px" }}>
                {step.name}
              </Title>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {step.path}
              </Text>
              {badges && badges.length > 0 && (
                <Space wrap>
                  {badges.map((badge, badgeIndex) => (
                    <Tooltip key={badgeIndex} title={badge.tooltip}>
                      <Tag color={badge.color} style={{ fontSize: "11px", margin: "2px 0" }}>
                        {badge.text}
                      </Tag>
                    </Tooltip>
                  ))}
                </Space>
              )}
            </Space>
          </div>
        </div>

        {/* Right Section - Metrics */}
        <div style={{ display: "flex", alignItems: "center", gap: "48px" }}>
          {/* Views */}
          <div style={{ textAlign: "center", width: "60px" }}>
            <Text type="secondary" style={{ fontSize: "11px", display: "block", textTransform: "uppercase" }}>
              Views
            </Text>
            <Title level={4}>{formatMetricValue(metrics.views)}</Title>
          </div>

          {/* Conversions */}
          <div style={{ textAlign: "center", width: "100px" }}>
            <Text type="secondary" style={{ fontSize: "11px", display: "block", textTransform: "uppercase" }}>
              Conversions
            </Text>
            <Title level={4} className="whitespace-nowrap">
              {step.type === FunnelStepType.ThankYou
                ? "N/A"
                : (() => {
                    const conversions =
                      step.type === FunnelStepType.Landing ? metrics.landingConversions : metrics.conversions;
                    if (metrics.views === 0) return "0%";
                    const percentage = ((conversions / metrics.views) * 100).toFixed(1);
                    return `${percentage}% (${conversions})`;
                  })()}
            </Title>
          </div>

          {/* Revenue */}
          <div style={{ textAlign: "center", width: "110px" }}>
            <Text type="secondary" style={{ fontSize: "11px", display: "block", textTransform: "uppercase" }}>
              Revenue
            </Text>
            <Title level={4}>
              {step.type === FunnelStepType.ThankYou || step.type === FunnelStepType.Landing
                ? "N/A"
                : metrics.revenue === 0
                ? "0.00"
                : formatPriceValue(metrics.revenue)}
            </Title>
          </div>
        </div>
      </div>
    </Card>
  );
};

interface ProductSalesFunnelsDetailsProps {
  funnel: SalesFunnel;
  funnelStats?: {
    viewed: number;
    purchased: number;
    rejected: number;
    revenue: number;
    stats: Record<string, StepStats>;
    testmodeStats: Record<string, StepStats>;
  };
  excludeTestEvents?: boolean;
  onExcludeTestEventsChange?: (checked: boolean) => void;
  dateRange?: { startDate?: string; endDate?: string };
  onDateRangeChange?: (dateRange: { startDate?: string; endDate?: string }) => void;
}

type StepStats = {
  viewed: number;
  purchased: number;
  rejected: number;
  revenue: number;
  conversion: number;
};

export const ProductSalesFunnelsDetails = ({
  funnel,
  funnelStats,
  excludeTestEvents,
  onExcludeTestEventsChange,
  dateRange,
  onDateRangeChange,
}: ProductSalesFunnelsDetailsProps) => {
  const [funnelSteps, setFunnelSteps] = useState(funnel.steps || []);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      setFunnelSteps((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const activeStep = funnelSteps.find((step) => step.id === activeId);

  // Show message if no steps available
  if (!funnelSteps || funnelSteps.length === 0) {
    return (
      <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto", textAlign: "center" }}>
        <Text type="secondary">No funnel steps available for this sales funnel.</Text>
      </div>
    );
  }

  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (onDateRangeChange) {
      if (dates?.[0] && dates?.[1]) {
        onDateRangeChange({
          startDate: dates[0].tz("Europe/Warsaw").format("YYYY-MM-DD"),
          endDate: dates[1].tz("Europe/Warsaw").format("YYYY-MM-DD"),
        });
      } else {
        onDateRangeChange({});
      }
    }
  };

  const dateRangeValue: [dayjs.Dayjs, dayjs.Dayjs] | null =
    dateRange?.startDate && dateRange?.endDate
      ? [dayjs(dateRange.startDate).tz("Europe/Warsaw"), dayjs(dateRange.endDate).tz("Europe/Warsaw")]
      : null;

  return (
    <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <div
        className="mb-6 flex flex-wrap items-center justify-between gap-4"
        style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}
      >
        {onExcludeTestEventsChange && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Switch checked={excludeTestEvents} onChange={onExcludeTestEventsChange} size="small" />
            <Text type="secondary">Odfiltruj testowe zdarzenia</Text>
          </div>
        )}

        {onDateRangeChange && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Text type="secondary">Zakres dat:</Text>
            <DatePicker.RangePicker
              value={dateRangeValue}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              placeholder={["Data początkowa", "Data końcowa"]}
              style={{ width: 300 }}
            />
          </div>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={funnelSteps.map((step) => step.id)} strategy={verticalListSortingStrategy}>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {funnelSteps.map((step) => (
              <SortableItem
                key={step.id}
                step={step}
                funnelStats={funnelStats}
                allSteps={funnelSteps}
                excludeTestEvents={excludeTestEvents ?? false}
              />
            ))}
          </Space>
        </SortableContext>

        <DragOverlay>
          {activeStep && (
            <FunnelStepCard
              step={activeStep}
              dragHandleProps={{}}
              isDragging={true}
              funnelStats={funnelStats}
              allSteps={funnelSteps}
              excludeTestEvents={excludeTestEvents ?? false}
            />
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
